/**
 * PricingMatrix - 定价卡片（Free + 三档付费）。
 *
 * 月/年切换；未登录/Free 用户点击付费档 CTA 创建 Checkout 跳转 Creem。
 * 已有活跃订阅的用户（2026-08-19 换档规则，2026-08-19 简化为三态 CTA）：
 * - 高于当前（含同档月付→年付视图）→「Upgrade」（下周期生效，二次确认提醒生效日期）
 * - 等于当前（同档同周期）→「Current plan」
 * - 低于当前（低档/同档年付→月付视图/Free）→「Subscribe」但不可点击（不支持降级）
 * 取消 / 升降级的完整说明见定价页 FAQ。
 * 未登录用户：CTA 包裹 Clerk <SignUpButton>（组件内部处理加载态，避免
 * "ClerkJS components are not ready yet" 错误）。
 */

import { useEffect, useState } from "react";
import { useUser, SignUpButton } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import {
  createCheckout,
  changeSubscription,
  getLicense,
  ApiError,
  type Tier,
  type BillingInterval,
  type LicenseInfo,
} from "../lib/api";
import { usePricing } from "../hooks/usePricing";
import { useLang, localizedPath } from "../lib/lang";
import { Check } from "./icons";

interface Plan {
  tier: Tier | "free";
  /** 月费；Free 为 0 */
  monthly: number;
  /** 年费；Free 为 0 */
  yearly: number;
  /** 每月积分额度（描述主信息） */
  credits: number;
  /** 授权 key（映射到 plan.license.*） */
  licenseKey: "personal" | "basic_commercial" | "full_commercial";
  /** Free 档：无导出/优先队列等付费特性，描述行不同 */
  isFree?: boolean;
  highlight?: boolean;
}

const PLANS: Plan[] = [
  {
    tier: "free",
    monthly: 0,
    yearly: 0,
    credits: 100,
    licenseKey: "personal",
    isFree: true,
  },
  {
    tier: "starter",
    monthly: 9.9,
    yearly: 99,
    credits: 1000,
    licenseKey: "personal",
  },
  {
    tier: "pro",
    monthly: 19.9,
    yearly: 199,
    credits: 2500,
    licenseKey: "basic_commercial",
    highlight: true,
  },
  {
    tier: "studio",
    monthly: 69.9,
    yearly: 699,
    credits: 10000,
    licenseKey: "full_commercial",
  },
];

const TIER_ORDER: Record<string, number> = { free: 0, starter: 1, pro: 2, studio: 3 };

/**
 * 订阅排序键：档位为主，同档年付 > 月付。
 * 用于三态 CTA 判定（同档月付→年付视图视为 Upgrade，反向视为低档不可点）。
 */
function planKey(tier: string, interval: BillingInterval): number {
  return TIER_ORDER[tier] * 2 + (interval === "year" ? 1 : 0);
}

/** 生效日期格式化（跟随当前语言） */
function formatPeriodEnd(ms: number | null, lang: string): string {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString(lang, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function PricingMatrix({
  interval,
}: {
  interval: BillingInterval;
}) {
  const { t, i18n } = useTranslation();
  const lang = useLang();
  const { isSignedIn, isLoaded } = useUser();
  const { pricing } = usePricing();
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [errorTier, setErrorTier] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [noticeTier, setNoticeTier] = useState<string | null>(null);
  const [noticeMsg, setNoticeMsg] = useState<string | null>(null);
  const [license, setLicense] = useState<LicenseInfo | null>(null);

  // 登录后拉当前订阅状态（档位/周期），驱动换档 CTA 矩阵
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      getLicense()
        .then(setLicense)
        .catch(() => setLicense(null));
    } else {
      setLicense(null);
    }
  }, [isLoaded, isSignedIn]);

  // 当前订阅档位（free = 无活跃订阅）
  const currentTier: string = license?.tier ?? "free";
  const hasActiveSub = currentTier !== "free";
  const subInterval = license?.subscriptionInterval ?? "month";
  const currentKey = hasActiveSub ? planKey(currentTier, subInterval) : -1;
  const periodEndDate = formatPeriodEnd(
    license?.subscriptionPeriodEnd ?? null,
    i18n.language,
  );

  function readableError(err: unknown): string {
    if (err instanceof ApiError && err.body) {
      const body = err.body as { error?: string; message?: string };
      // 首月试购价生效期内换档被 Creem 拒绝（平台限制）→ 用本地化文案
      //（后端 message 为英文，这里优先走 i18n 保证四语一致）
      if (body.error === "upgrade_blocked_by_discount") {
        return t("plan.error.upgradeBlockedByDiscount");
      }
      // 其余（creem_error / downgrade_not_allowed 等）优先用后端返回的可读 message
      if (body.message) return body.message;
    }
    return err instanceof Error ? err.message : t("plan.error.somethingWrong");
  }

  async function handleSelect(plan: Plan) {
    setLoadingTier(plan.tier);
    setErrorTier(null);
    setErrorMsg(null);
    setNoticeTier(null);
    setNoticeMsg(null);
    try {
      const result = await createCheckout({
        type: "subscription",
        tier: plan.tier as Tier,
        interval,
      });
      // Creem 返回空 url 视为后端异常（如 product id 未配置）
      if (!result.url) {
        throw new Error(t("plan.error.checkoutUnavailable"));
      }
      window.location.href = result.url;
    } catch (err) {
      setLoadingTier(null);
      setErrorTier(plan.tier);
      setErrorMsg(readableError(err));
    }
  }

  /** 升级（含同档换周期，统一下周期生效，二次确认显式提醒生效日期） */
  async function handleUpgrade(plan: Plan) {
    // 显式提醒：新套餐在当前周期结束后生效，当前权益不变
    const tierName = t(`plan.tier.${plan.tier}`);
    const ok = window.confirm(
      t("plan.confirm.change", {
        tier: tierName,
        interval: t(`pricing.interval.${interval}`),
        date: periodEndDate,
      }),
    );
    if (!ok) return;
    setLoadingTier(plan.tier);
    setErrorTier(null);
    setErrorMsg(null);
    setNoticeTier(null);
    setNoticeMsg(null);
    try {
      await changeSubscription(plan.tier as Tier, interval);
      setLoadingTier(null);
      setNoticeTier(plan.tier);
      setNoticeMsg(t("plan.success.changed", { tier: tierName, date: periodEndDate }));
    } catch (err) {
      setLoadingTier(null);
      setErrorTier(plan.tier);
      setErrorMsg(readableError(err));
    }
  }

  // 三态明确分离，避免 Clerk 加载中（isLoaded=false）误挂 SignUpButton：
  // 已登录用户在 single-session 应用下挂载 SignUpButton 会触发 Clerk no-op 警告。
  const isAuthed = isLoaded && isSignedIn;
  // 仅当 Clerk 加载完成且确认为未登录时，才渲染 SignUpButton
  const signedOut = isLoaded && !isSignedIn;

  return (
    <div className="pricing-matrix">
      {PLANS.map((plan) => {
        // 实时价格（后端从 Creem 读）：优先用 Creem 价，未取到/加载中用硬编码兜底。
        // free 不在 tiers 里（永远 $0）。
        const fallback = interval === "month" ? plan.monthly : plan.yearly;
        const live =
          plan.tier !== "free" ? pricing?.tiers[plan.tier]?.[interval] : undefined;
        const price = live ?? fallback;
        // M3.9 首月试购价：月付卡片首期 5 折（$4.95/$9.95/$34.95）。
        // 展示层不区分资格，统一显示首月价；真实资格由 checkout 层按
        // 订阅历史裁决（首订才预置券码）。年付不参与。
        const firstMonthPrice = !plan.isFree && interval === "month" ? price / 2 : null;
        // 常规价格展示（1 位小数；整数值如年费 99 去掉尾随 .0）
        const priceLabel = plan.isFree
          ? price.toFixed(0)
          : price.toFixed(1).replace(/\.0$/, "");
        // 周期后缀四卡统一（Free 也显示 /月，保持价格行一致）
        const periodLabel = t(`plan.period.${interval}`);
        const planName = t(`plan.tier.${plan.tier}`);
        // 登录回跳 URL：保留当前语言前缀（localizedPath 补全）
        const redirectUrl = localizedPath(window.location.pathname, lang);
        // 三态判定（仅对已登录且有活跃订阅的用户生效）
        const key = planKey(plan.tier, interval);
        const mode: "upgrade" | "current" | "lower" | null = !isAuthed
          ? null
          : !hasActiveSub
            ? plan.isFree
              ? "current"
              : null // free 用户：付费档正常购买
            : key > currentKey
              ? "upgrade"
              : key === currentKey
                ? "current"
                : "lower";
        return (
          <div
            key={plan.tier}
            className={`plan-card ${plan.highlight ? "highlight" : ""}`}
          >
            {plan.highlight && (
              <span className="popular-badge">{t("plan.cta.mostPopular")}</span>
            )}
            {/* M3.9 首月半价角标：月付付费卡右上角（Free/年付不参与）；
                与 popular-badge（顶部居中）错位，窄屏由媒体查询缩小避免相碰 */}
            {firstMonthPrice !== null && (
              <span className="first-month-badge">{t("plan.firstMonth.badge")}</span>
            )}
            <h3 className="plan-name">{planName}</h3>
            <div className="plan-price">
              <span className="amount">
                {firstMonthPrice !== null
                  ? `$${firstMonthPrice.toFixed(2)}`
                  : `$${priceLabel}`}
              </span>
              <span className="period">{periodLabel}</span>
            </div>
            {/* M3.9 首月试购价：划线原价 + "then $X/mo"。
                月付视图四张卡统一渲染本段（Free 无折扣内容 → 隐藏占位），
                保证特性列表与 CTA 在所有卡片上起始位置对齐；年付视图不渲染。 */}
            {interval === "month" && (
              <p
                className={`plan-first-month${firstMonthPrice === null ? " plan-first-month-empty" : ""}`}
                aria-hidden={firstMonthPrice === null}
              >
                {firstMonthPrice !== null && (
                  <>
                    <s>${priceLabel}</s>
                    {t("plan.firstMonth.then", { price: `$${priceLabel}` })}
                  </>
                )}
              </p>
            )}
            <ul className="plan-features">
              {/* credits 行含 HTML（<strong>），用 dangerouslySetInnerHTML 渲染插值结果 */}
              <li>
                <Check size={14} className="plan-feature-check" />
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("plan.feature.credits", {
                      count: plan.credits.toLocaleString(),
                    }),
                  }}
                />
              </li>
              {plan.isFree ? (
                <li>
                  <Check size={14} className="plan-feature-check" />
                  {t("plan.feature.slowQueue")}
                </li>
              ) : (
                <li>
                  <Check size={14} className="plan-feature-check" />
                  {t(
                    plan.tier === "studio"
                      ? "plan.feature.priorityHigh"
                      : "plan.feature.priority",
                  )}
                </li>
              )}
              <li>
                <Check size={14} className="plan-feature-check" />
                {t(`plan.license.${plan.licenseKey}`)}
              </li>
              <li>
                <Check size={14} className="plan-feature-check" />
                {plan.isFree
                  ? t("plan.feature.plaza")
                  : t("plan.feature.plazaDownload")}
              </li>
              <li>
                <Check size={14} className="plan-feature-check" />
                {t("plan.feature.export")}
              </li>
            </ul>
            {plan.isFree ? (
              isAuthed ? (
                mode === "current" ? (
                  <button className="plan-cta" disabled>
                    {t("plan.cta.current")}
                  </button>
                ) : (
                  // 付费用户：Free 低于当前订阅 → Subscribe 不可点（取消请见 FAQ / 我的订阅）
                  <button className="plan-cta" disabled>
                    {t("plan.cta.get")}
                  </button>
                )
              ) : signedOut ? (
                <SignUpButton mode="modal" forceRedirectUrl={redirectUrl}>
                  <button className="plan-cta">
                    {t("plan.cta.getStarted")}
                  </button>
                </SignUpButton>
              ) : (
                <button className="plan-cta" disabled>
                  {t("plan.cta.loading")}
                </button>
              )
            ) : isAuthed && mode === "upgrade" ? (
              <button
                className="plan-cta"
                onClick={() => handleUpgrade(plan)}
                disabled={loadingTier === plan.tier}
              >
                {loadingTier === plan.tier
                  ? t("plan.cta.loading")
                  : t("plan.cta.upgrade")}
              </button>
            ) : isAuthed && mode === "current" ? (
              <button className="plan-cta" disabled>
                {t("plan.cta.current")}
              </button>
            ) : isAuthed && mode === "lower" ? (
              // 低于当前订阅（不支持降级）：Subscribe 不可点，说明见 FAQ
              <button className="plan-cta" disabled>
                {t("plan.cta.get")}
              </button>
            ) : isAuthed ? (
              // free 用户：正常购买
              <button
                className="plan-cta"
                onClick={() => handleSelect(plan)}
                disabled={loadingTier === plan.tier}
              >
                {loadingTier === plan.tier
                  ? t("plan.cta.redirecting")
                  : t("plan.cta.get")}
              </button>
            ) : signedOut ? (
              <SignUpButton mode="modal" forceRedirectUrl={redirectUrl}>
                <button className="plan-cta">{t("plan.cta.get")}</button>
              </SignUpButton>
            ) : (
              <button className="plan-cta" disabled>
                {t("plan.cta.loading")}
              </button>
            )}
            {noticeTier === plan.tier && noticeMsg && (
              <p className="plan-notice" role="status">
                {noticeMsg}
              </p>
            )}
            {errorTier === plan.tier && errorMsg && (
              <p className="plan-error" role="alert">
                {errorMsg}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
